import type { RegistrarInteresseDto } from '@social/shared';

/**
 * Cliente da captura de e-mail do funil (`POST /api/interesse`).
 *
 * Mesmo desenho de `lib/auditoria.ts`: caminho relativo (proxeado pelo Vite
 * em dev e pelo nginx do site em produção) e `buscar` injetável só para o
 * teste não depender de rede de verdade.
 */
export class ErroInteresse extends Error {
  constructor(
    readonly codigo: string,
    mensagem: string,
    readonly status: number,
    /** Só vem preenchido em 429 (`limite_ip`) — o cabeçalho `Retry-After`. */
    readonly retryAfterSegundos?: number,
    /** Só vem preenchido em 422 (`entrada_invalida`) — qual campo reprovou. */
    readonly campo?: string,
  ) {
    super(mensagem);
    this.name = 'ErroInteresse';
  }
}

interface EnvelopeErro {
  erro?: {
    codigo?: string;
    mensagem?: string;
    detalhes?: { campo?: string; mensagem?: string }[];
  };
}

export interface DependenciasInteresse {
  buscar: typeof fetch;
}

export async function registrarInteresse(
  dto: RegistrarInteresseDto,
  dep: DependenciasInteresse = { buscar: fetch },
): Promise<void> {
  const resposta = await dep.buscar('/api/interesse', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(dto),
  });

  if (resposta.ok) return;

  const json = (await resposta.json().catch(() => ({}))) as EnvelopeErro;
  const retryAfter = resposta.headers.get('retry-after');
  // Em 422 (`entrada_invalida`), a `mensagem` do topo é genérica ("Confira os
  // campos destacados.") — o texto que ajuda quem preenche é o do PRIMEIRO
  // detalhe (`detalhes[0].mensagem`), o único campo real deste formulário.
  const detalhe = json.erro?.detalhes?.[0];
  throw new ErroInteresse(
    json.erro?.codigo ?? 'desconhecido',
    detalhe?.mensagem ??
      json.erro?.mensagem ??
      'Não foi possível concluir. Tente novamente.',
    resposta.status,
    retryAfter ? Number(retryAfter) : undefined,
    detalhe?.campo,
  );
}
